import type { SearchEvent, SearchMarket, SearchScope } from '../api/searchTypes';
import { formatCents, formatDate, formatUsd } from './searchFormat';

export interface ResultColumn {
    key: string;
    label: string;
    width: number;
    sort?: string;
    align?: 'left' | 'right';
    eventValue: (
        event: SearchEvent,
        primary: SearchMarket | null,
        scope: SearchScope,
    ) => string;
    marketValue: (market: SearchMarket) => string;
}

export const TITLE_MIN_WIDTH = 320;
export const ACTION_COLUMN_WIDTH = 48;
export const SELECT_COLUMN_WIDTH = 30;

const COLUMNS: ResultColumn[] = [
    {
        key: 'status',
        label: 'Status',
        width: 60,
        align: 'left',
        eventValue: e => e.status,
        marketValue: m => m.status,
    },
    {
        key: 'price',
        label: 'Price',
        width: 120,
        sort: 'yes_prob',
        eventValue: e => formatCents(e.yes_prob),
        marketValue: m => formatCents(m.yes_prob),
    },
    {
        key: 'volume',
        label: 'Volume',
        width: 84,
        sort: 'volume',
        eventValue: e => formatUsd(e.volume),
        marketValue: m => formatUsd(m.volume),
    },
    {
        key: 'markets',
        label: 'Markets',
        width: 76,
        sort: 'market_count',
        eventValue: (e, _p, scope) =>
            scope === 'market'
                ? `${e.matched_count} / ${e.market_count}`
                : `${e.open_market_count} / ${e.market_count}`,
        marketValue: () => '',
    },
    {
        key: 'started',
        label: 'Started',
        width: 88,
        sort: 'start_date',
        eventValue: e => formatDate(e.start_date),
        marketValue: m => formatDate(m.effective_start_date),
    },
    {
        key: 'closed',
        label: 'Closed',
        width: 88,
        sort: 'closed_date',
        eventValue: e => formatDate(e.closed_date),
        marketValue: m => formatDate(m.closed_date),
    },
    {
        key: 'resolved',
        label: 'Resolved',
        width: 88,
        sort: 'resolved_date',
        eventValue: e => formatDate(e.resolved_date),
        marketValue: m => formatDate(m.resolved_date),
    },
    {
        key: 'created',
        label: 'Created',
        width: 88,
        sort: 'created_at',
        eventValue: e => formatDate(e.created_at),
        marketValue: () => '',
    },
    {
        key: 'end',
        label: 'Planned end',
        width: 92,
        sort: 'end_date',
        eventValue: e => formatDate(e.end_date),
        marketValue: m => formatDate(m.end_date),
    },
];

export function resultColumns(): ResultColumn[] {
    return COLUMNS;
}

export function gridTemplate(): string {
    const cells = COLUMNS.map(c => `${c.width}px`).join(' ');
    return (
        `minmax(${TITLE_MIN_WIDTH}px, 1fr) ${cells} ` +
        `${ACTION_COLUMN_WIDTH}px ${SELECT_COLUMN_WIDTH}px`
    );
}

export function minTableWidth(): number {
    return (
        TITLE_MIN_WIDTH +
        ACTION_COLUMN_WIDTH +
        SELECT_COLUMN_WIDTH +
        COLUMNS.reduce((sum, c) => sum + c.width + 8, 0)
    );
}